import * as THREE from 'three';
import { Vec3 } from '@/core/math/Vec3';
import { GLSL_CONSTANTS } from './shaders/common.glsl';

/**
 * The star's glare.
 *
 * A star's disc is about half a degree across, and drawing it at that angular
 * size — which the astronomical layer correctly does — produces a small white
 * dot. That is geometrically right and completely wrong perceptually: what
 * makes the sun read as the sun is not its size but the fact that it saturates
 * everything near it. Real eyes and real lenses both spread that saturation
 * over a wide halo, and a renderer that stops at the disc looks like it has a
 * bright pixel rather than a star.
 *
 * So the disc keeps its true angular size and this adds what the optics would:
 * a broad halo, a tighter core bloom and a few diffraction streaks, all
 * additive. It is a camera artefact rendered honestly rather than a fake sun,
 * and it scales with the actual irradiance, so it is overwhelming at Vesper and
 * a modest spark out at Kolyma.
 */

const VERTEX = /* glsl */ `
varying vec2 vUv;
void main() {
  vUv = uv * 2.0 - 1.0;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const FRAGMENT = /* glsl */ `
precision highp float;

${GLSL_CONSTANTS}

varying vec2 vUv;

uniform vec3 uColor;
uniform float uIntensity;
uniform float uStreaks;
/** The star's true angular radius, as a fraction of the quad's half-angle. */
uniform float uDiscRadius;
/** 0 in vacuum, 1 in thick air. Gates the aureole and only the aureole. */
uniform float uAir;
/** How fierce the star is here, 0..1. Widens the glare instead of clipping it. */
uniform float uBloom;

// --- Tunables ------------------------------------------------------------
// uDiscRadius is never allowed smaller than this fraction of the quad, so a
// star with zero (or degenerate) angular size never divides down to a
// zero-width disc/PSF.
const float MIN_DISC_RADIUS = 0.002;

// 1. Disc: the star's true angular size in HDR. The narrow edge transition is
// what prevents tone mapping from turning the core into a polygonal white cog.
const float DISC_EDGE_INNER_FRAC = 0.90;
const float DISC_EDGE_OUTER_FRAC = 1.10;
const float DISC_PEAK = 14.0;

// How much uBloom widens every profile below (PSF, spikes, ring): a fiercer
// star's veiling glare genuinely spreads further in a real optic, so it
// grows rather than merely clipping harder.
const float BLOOM_SPREAD_GAIN = 1.25;

// 2. Instrument point spread: a tight Moffat core plus a shallow far wing.
const float PSF_CORE_ALPHA_MULT = 1.65;
const float PSF_CORE_BETA = 2.8;
const float PSF_CORE_STRENGTH = 1.5;
const float PSF_WING_ALPHA_MULT = 6.5;
const float PSF_WING_BETA = 2.1;
const float PSF_WING_STRENGTH = 0.10;

// 3. Diffraction spikes: six main rays 60 degrees apart.
const float SPIKE_WIDTH_FRAC = 0.16;
const float SPIKE_LENGTH_FRAC = 11.0;
const float SPIKE_LENGTH_BLOOM_GAIN = 7.0;
const float SPIKE_STRENGTH = 0.24;

// 4. Aureole: Mie forward scattering by aerosols, gated entirely by uAir (see
// its uniform doc). Tightened to concentrate near the disc rather than
// reading as fog filling the sky — see main()'s comment for the history.
const float AUREOLE_TIGHT_RADIUS_FRAC = 8.0;
const float AUREOLE_TIGHT_BLOOM_GAIN = 6.0;
const float AUREOLE_TIGHT_STRENGTH = 0.16;
const float AUREOLE_WING_ALPHA = 0.18;
const float AUREOLE_WING_BLOOM_GAIN = 0.08;
const float AUREOLE_WING_BETA = 2.0;
const float AUREOLE_WING_STRENGTH = 0.055;

// Final window: fades the whole profile to nothing before the quad's own
// edge, so no term's tail ends on a hard, unphysical circle at r = 1.
const float RIM_FADE_START = 1.0;
const float RIM_FADE_END = 0.70;

/**
 * Moffat profile.
 *
 * Astronomers fit real stellar point spreads with this rather than a Gaussian
 * for exactly the reason that matters here: the wings. A Gaussian falls off far
 * too fast, so a bright point becomes a soft blob with a definite edge. A
 * Moffat keeps a tight core and faint skirts that reach a long way, which is
 * what a star looks like through any real optic.
 */
float moffat(float r2, float alpha, float beta) {
  return pow(1.0 + r2 / (alpha * alpha), -beta);
}

void main() {
  float r = length(vUv);
  if (r > 1.0) discard;
  float r2 = r * r;
  float a = max(uDiscRadius, MIN_DISC_RADIUS);

  // 1. The disc: saturated across the star's TRUE angular size.
  //
  //    The core remains well above display white, but only inside the physical
  //    angular disc. Exposure rolls the surrounding optical falloff through
  //    the ACES shoulder instead of widening a fixed white plate.
  float disc = (1.0 - smoothstep(a * DISC_EDGE_INNER_FRAC, a * DISC_EDGE_OUTER_FRAC, r)) * DISC_PEAK;

  // 2. Instrument point spread — the part that survives in vacuum, because it
  //    is the optics and not the air. Two Moffat terms, a tight core and a
  //    shallow far wing, both widened by uBloom: veiling glare in a real optic
  //    genuinely spreads further for a brighter source, so a fierce star grows
  //    rather than merely clipping harder.
  float spread = 1.0 + uBloom * BLOOM_SPREAD_GAIN;
  float psf = moffat(r2, a * PSF_CORE_ALPHA_MULT * spread, PSF_CORE_BETA) * PSF_CORE_STRENGTH
            + moffat(r2, a * PSF_WING_ALPHA_MULT * spread, PSF_WING_BETA) * PSF_WING_STRENGTH;

  // 3. Diffraction spikes — thin rays, not wedges.
  //
  //    The previous version thresholded on ANGLE alone ('cos(angle * 3.0)'
  //    raised to a high power). That is constant width IN ANGLE, and a shape
  //    of constant angular width necessarily gets wider — not narrower — as r
  //    grows, because angular width times radius is linear in radius. It is a
  //    pie slice, not a ray, and widening toward the frame edge is exactly
  //    backwards from a real aperture's diffraction spikes, which hold
  //    roughly constant width in DISTANCE from their own axis and taper only
  //    in brightness. That mismatch is what actually put the "wide wedge" in
  //    the old comment here — the code it was warning against.
  //
  //    Six rays, three lines through the centre, each 60 degrees apart.
  //    'sector' is the signed angle from the nearest line, so
  //    'r * sin(sector)' is the exact perpendicular distance from the
  //    fragment to that line and 'r * cos(sector)' is the exact distance
  //    along it — both plain trigonometry on a point already in polar form,
  //    not an approximation.
  float angle = atan(vUv.y, vUv.x);
  const float SECTOR = PI / 3.0;
  float sector = angle - SECTOR * floor(angle / SECTOR + 0.5);
  float perp = abs(r * sin(sector));
  float along = abs(r * cos(sector));
  float rayWidth = max(a * SPIKE_WIDTH_FRAC * spread, 1e-5);
  float rayLength = max(a * (SPIKE_LENGTH_FRAC + uBloom * SPIKE_LENGTH_BLOOM_GAIN), 1e-5);
  float spikes = exp(-perp / rayWidth) * exp(-along / rayLength)
               * uStreaks * SPIKE_STRENGTH;

  // 4. The aureole, and the whole point of the earlier rewrite this continues:
  //    the broad soft glow around the sun is Mie forward scattering by
  //    aerosols in the air. In vacuum there is nothing to scatter it, so it
  //    must vanish. Drawing it unconditionally is what made the star read as
  //    a hazy dome from orbit and on airless worlds — an atmospheric effect
  //    with no atmosphere present.
  //
  //    Tightened again here: the previous falloff ('exp(-r*3.6)' plus a
  //    'moffat' with alpha 0.5) stayed within a factor of 2 of its own peak
  //    out to about a third of the quad's radius, which is what actually made
  //    the glare look like fog filling the sky rather than a glow around a
  //    point — captured evidence (SwiftShader, scripts/scratch-capture.mjs)
  //    showed a flat, undifferentiated dome with no visible falloff structure
  //    behind the disc. A steeper exponential plus a narrower Moffat alpha
  //    keeps the SAME growth with 'uAir'/brightness but concentrates it near
  //    the disc, leaving the outer quad legibly darker instead of uniformly
  //    milky.
  float aureoleRadius = max(
    a * (AUREOLE_TIGHT_RADIUS_FRAC + uBloom * AUREOLE_TIGHT_BLOOM_GAIN),
    1e-5
  );
  float aureole = uAir * (
    exp(-r / aureoleRadius) * AUREOLE_TIGHT_STRENGTH
    + moffat(
        r2,
        AUREOLE_WING_ALPHA + uBloom * AUREOLE_WING_BLOOM_GAIN,
        AUREOLE_WING_BETA
      ) * AUREOLE_WING_STRENGTH
  );

  // Fade to nothing before the quad's own edge.
  //
  // Any profile with tails needs an explicit window; otherwise the quad itself
  // becomes visible as a hard circular boundary.
  float rim = smoothstep(RIM_FADE_START, RIM_FADE_END, r);

  vec3 optical = uColor * (disc + psf + spikes);
  vec3 atmospheric = mix(uColor, vec3(1.0, 0.72, 0.42), 0.42) * aureole;
  gl_FragColor = vec4((optical + atmospheric) * uIntensity * rim, 1.0);

  // See TerrainMaterial. Tone mapping matters more here than anywhere: the core
  // is deliberately far above 1.0, and without a curve to roll it off it simply
  // clips to a flat white plate.
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;

/**
 * How fierce the star reads at a given local irradiance, 0..1.
 *
 * Shared between the glare quad's own sizing (below) and `Game`'s exposure
 * response, which reduces exposure when the sun sits in frame — a real
 * camera's metering reacting to the same "how bright is the thing I'm
 * looking at" question the glare answers for its own sizing. Kept as one
 * function so the two never drift apart.
 */
/** Solar constant at 1 AU, W/m^2 — the reference irradiance bloom is relative to. */
const SOLAR_CONSTANT_1AU = 1361;
/** Floor on the relative-irradiance ratio, so a body with no star overhead still returns a small positive bloom rather than 0^x. */
const MIN_RELATIVE_IRRADIANCE = 0.02;
/** Compression exponent: a fifth root leaves about a factor of three across a ~400x range of irradiance. */
const BLOOM_COMPRESSION_EXPONENT = 0.2;
/** Overall bloom gain after compression. */
const BLOOM_GAIN = 0.62;
const MIN_TONE_MAPPING_EXPOSURE = 0.02;
const MAX_TONE_MAPPING_EXPOSURE = 12;

export function sunBloomFactor(irradiance: number, exposure = 1): number {
  // Optical bloom is the portion of the point-spread function that remains
  // above the display threshold after exposure. Folding the current exposure
  // into the effective irradiance lets a stopped-down view contract naturally
  // and a dark-adapted view spread, while the logarithmic response prevents
  // either extreme from taking over the frame.
  const exposureScale = THREE.MathUtils.clamp(
    exposure,
    MIN_TONE_MAPPING_EXPOSURE,
    MAX_TONE_MAPPING_EXPOSURE,
  );
  const relative = Math.max(
    MIN_RELATIVE_IRRADIANCE,
    (irradiance / SOLAR_CONSTANT_1AU) * exposureScale,
  );
  // Compressed hard: irradiance spans a factor of four hundred across the
  // system and neither the glare nor the metering response should, or
  // Vesper's fills the screen (and blacks out everything else) while
  // Kolyma's vanishes. A fifth root leaves about a factor of three.
  return Math.min(1, Math.pow(relative, BLOOM_COMPRESSION_EXPONENT) * BLOOM_GAIN);
}

/**
 * One "ghost" artefact in the flare chain, in the anamorphic-lens sense: an
 * internal reflection between lens elements, which is why every ghost lies on
 * the straight line through the screen centre and the light itself.
 *
 * `t` is position along that line: 0 sits on the light, 0.5 is screen centre,
 * 1 is the mirror image of the light on the far side, and a little past 1 is
 * the common look of a chain that overshoots slightly rather than landing
 * exactly opposite.
 */
interface GhostSpec {
  t: number;
  size: number;
  color: THREE.Color;
  opacity: number;
}

const GHOST_CHAIN: GhostSpec[] = [
  { t: 0.34, size: 0.028, color: new THREE.Color(0x83b7ae), opacity: 0.045 },
  { t: 0.72, size: 0.052, color: new THREE.Color(0xb78d64), opacity: 0.055 },
  { t: 1.06, size: 0.082, color: new THREE.Color(0xa66655), opacity: 0.035 },
];

/** Builds a faint circular coating reflection with a soft annular rim. */
function buildGhostTexture(): THREE.CanvasTexture {
  const size = 128;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const cx = size / 2;
  const cy = size / 2;
  const radius = size * 0.48;

  const gradient = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius);
  gradient.addColorStop(0, 'rgba(255,255,255,0.08)');
  gradient.addColorStop(0.52, 'rgba(255,255,255,0.025)');
  gradient.addColorStop(0.72, 'rgba(255,255,255,0.18)');
  gradient.addColorStop(0.86, 'rgba(255,255,255,0.06)');
  gradient.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, size, size);

  const texture = new THREE.CanvasTexture(canvas);
  texture.needsUpdate = true;
  return texture;
}

// --- Glare sizing/intensity tunables (see update()) -------------------------
const MIN_STAR_DISTANCE = 1e-6;       // below this the star is at the camera; nothing sensible to draw
const MIN_VISIBLE_VISIBILITY = 0.001; // occlusion fraction below which the glare is not worth drawing
/** Air density (kg/m3-ish game units) at which `air` saturates to 1. */
const REFERENCE_AIR_DENSITY = 1.0;
const VACUUM_HALF_ANGLE = 0.03;   // quad half-angle, radians, with no air present
const AIR_HALF_ANGLE_GAIN = 0.045; // additional half-angle at full air density
const BLOOM_ANGLE_FLOOR = 0.75;    // half-angle multiplier at zero bloom
const BLOOM_ANGLE_GAIN = 0.75;     // additional half-angle at full bloom
const MAX_DISC_RADIUS_FRACTION = 0.6; // caps uDiscRadius so the true disc can never exceed most of the quad
const INTENSITY_FLOOR = 1.2;
const INTENSITY_BLOOM_GAIN = 1.6;

// --- Ghost chain tunables (see updateGhosts()) -------------------------------
const GHOST_BEHIND_CAMERA_EPS = 1e-4; // local.z threshold below which the star counts as "in front"
/** Ghost distance from the camera, render units — arbitrary; only the sprites' angular position matters, not depth. */
const GHOST_DISTANCE = 40;
const EDGE_FADE_RANGE = 0.6;    // NDC distance past the frame edge over which the chain fades out
const CENTER_FADE_START = 0.06; // NDC distance from screen centre where the chain starts fading in
const CENTER_FADE_END = 0.3;    // NDC distance from screen centre where the chain reaches full strength
const GHOST_STRENGTH_FLOOR = 0.08;
const GHOST_STRENGTH_BLOOM_GAIN = 0.5;
const GHOST_VISIBILITY_THRESHOLD = 0.004; // below this a ghost sprite is hidden rather than drawn near-transparent
const GHOST_SPRITE_SCALE = 0.35;          // sprite scale relative to spec.size * GHOST_DISTANCE

export class SunGlare {
  readonly root = new THREE.Group();
  readonly mesh: THREE.Mesh;
  private readonly ghostTexture: THREE.CanvasTexture;
  private readonly ghosts: { sprite: THREE.Sprite; material: THREE.SpriteMaterial; spec: GhostSpec }[] = [];
  private readonly uniforms: {
    uColor: THREE.IUniform<THREE.Color>;
    uIntensity: THREE.IUniform<number>;
    uStreaks: THREE.IUniform<number>;
    uDiscRadius: THREE.IUniform<number>;
    uAir: THREE.IUniform<number>;
    uBloom: THREE.IUniform<number>;
  };

  constructor() {
    this.uniforms = {
      uColor: { value: new THREE.Color(1, 0.95, 0.86) },
      uIntensity: { value: 0 },
      uStreaks: { value: 1 },
      uDiscRadius: { value: 0.13 },
      uAir: { value: 0 },
      uBloom: { value: 0.5 },
    };

    const material = new THREE.ShaderMaterial({
      uniforms: this.uniforms as unknown as Record<string, THREE.IUniform>,
      vertexShader: VERTEX,
      fragmentShader: FRAGMENT,
      transparent: true,
      depthWrite: false,
      // NOT depth tested, and the occlusion is analytic instead — see the
      // `visibility` argument to update().
      //
      // Depth testing a glare billboard against terrain slices it along the
      // triangles of whatever ridge the star is behind, which is visible as a
      // dome with a bite taken out of it. That is backwards: glare is an
      // artefact of the optics, formed after the image, so it spills *over* an
      // occluding edge rather than being cut by it. Every real photograph of a
      // sunrise behind a mountain shows the glow washing across the ridgeline.
      depthTest: false,
      blending: THREE.AdditiveBlending,
    });

    this.mesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), material);
    this.mesh.frustumCulled = false;
    // After the discs, so it lies over the star rather than under it.
    this.mesh.renderOrder = 20;
    this.root.add(this.mesh);

    this.ghostTexture = buildGhostTexture();
    for (const spec of GHOST_CHAIN) {
      const ghostMaterial = new THREE.SpriteMaterial({
        map: this.ghostTexture,
        color: spec.color,
        transparent: true,
        depthTest: false,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        opacity: spec.opacity,
      });
      const sprite = new THREE.Sprite(ghostMaterial);
      sprite.frustumCulled = false;
      sprite.renderOrder = 19;
      sprite.visible = false;
      this.root.add(sprite);
      this.ghosts.push({ sprite, material: ghostMaterial, spec });
    }
  }

  /**
   * Places and sizes the glare.
   *
   * @param starRenderPosition star centre relative to the camera, render units
   * @param irradiance local stellar irradiance, W/m^2
   */
  update(
    starRenderPosition: Vec3,
    irradiance: number,
    cameraQuaternion: THREE.Quaternion,
    trueAngularRadius: number,
    airDensity: number,
    visibility: number,
    fovDegrees: number,
    aspect: number,
    exposure = 1,
  ): void {
    const distance = starRenderPosition.length();
    if (distance < MIN_STAR_DISTANCE || visibility <= MIN_VISIBLE_VISIBILITY) {
      this.mesh.visible = false;
      this.hideGhosts();
      return;
    }
    this.mesh.visible = true;
    this.mesh.position.set(starRenderPosition.x, starRenderPosition.y, starRenderPosition.z);
    // Billboard: the glare is an artefact of the optics, so it always faces the
    // lens rather than sitting in the world.
    this.mesh.quaternion.copy(cameraQuaternion);

    const air = Math.min(1, airDensity / REFERENCE_AIR_DENSITY);
    const bloom = sunBloomFactor(irradiance, exposure);

    // The quad only has to be wide enough to hold the faint wings. It is NOT the
    // size of the glare, which is what the previous version effectively made it:
    // a 10-degree quad with an exponential skirt across all of it, so the star
    // read as a dome of that size everywhere. In air the aureole genuinely is
    // several degrees wide, so the quad grows with density; in vacuum it stays
    // narrow because there is nothing out there to light up.
    // Grows with brightness, so a fiercer star reads as a wider glow rather
    // than a harder-clipped disc — the whole point of the change.
    const halfAngle = (VACUUM_HALF_ANGLE + air * AIR_HALF_ANGLE_GAIN) * (BLOOM_ANGLE_FLOOR + bloom * BLOOM_ANGLE_GAIN);
    this.mesh.scale.setScalar(distance * halfAngle);

    // The disc's own angular size, expressed against that half-angle. This is
    // what ties every term in the shader to reality: at 1 AU the sun's angular
    // radius is 0.0047 rad, so against a 0.035 rad quad the saturated core
    // occupies about 13% of the radius and the rest is falloff.
    this.uniforms.uDiscRadius.value = Math.min(MAX_DISC_RADIUS_FRACTION, trueAngularRadius / halfAngle);
    this.uniforms.uAir.value = air;
    this.uniforms.uBloom.value = bloom;

    // Enough that the physical disc saturates while the PSF remains on the
    // tone curve. Exposure changes both the shader output and `bloom`, so the
    // visible saturated radius contracts when the camera stops down.
    this.uniforms.uIntensity.value = (INTENSITY_FLOOR + bloom * INTENSITY_BLOOM_GAIN) * visibility;

    this.updateGhosts(starRenderPosition, distance, cameraQuaternion, fovDegrees, aspect, bloom, visibility);
  }

  /**
   * The ghost chain: internal-reflection artefacts strung along the line
   * through screen centre and the light source. Recomputed from the star's
   * ACTUAL direction and the camera's current orientation every call, rather
   * than cached — a chain that didn't track the sun's screen position as the
   * camera turns would be worse than no chain at all, since a real one is
   * defined entirely by where the source sits in frame.
   */
  private updateGhosts(
    starRenderPosition: Vec3,
    distance: number,
    cameraQuaternion: THREE.Quaternion,
    fovDegrees: number,
    aspect: number,
    bloom: number,
    visibility: number,
  ): void {
    // World-space direction to the star, expressed in the camera's own local
    // axes (x = right, y = up, z: the camera looks down -Z, so "in front"
    // means z < 0). A plain inverse rotation, not a projection-matrix trick —
    // consistent with computing bearing as a change of basis rather than
    // touching a THREE camera's matrices directly.
    const dir = new THREE.Vector3(
      starRenderPosition.x / distance,
      starRenderPosition.y / distance,
      starRenderPosition.z / distance,
    );
    const inverseQuat = cameraQuaternion.clone().invert();
    const local = dir.applyQuaternion(inverseQuat);

    if (local.z >= -GHOST_BEHIND_CAMERA_EPS) {
      // Behind the camera: a real flare chain fades out as the source leaves
      // frame rather than reappearing mirrored, so just hide it.
      this.hideGhosts();
      return;
    }

    const tanHalfY = Math.tan((fovDegrees * Math.PI) / 360);
    const tanHalfX = tanHalfY * aspect;
    const ndcX = local.x / -local.z / tanHalfX;
    const ndcY = local.y / -local.z / tanHalfY;

    // Chain fades out once the source is well outside the frame — some
    // allowance past the edge (real flares persist briefly as the source
    // exits), but not indefinitely.
    const offScreen = Math.max(Math.abs(ndcX), Math.abs(ndcY));
    const edgeFade = 1 - THREE.MathUtils.clamp((offScreen - 1) / EDGE_FADE_RANGE, 0, 1);

    // Every ghost's position is the light's own NDC position scaled by its
    // `factor`, so a light sitting exactly on screen centre puts every ghost
    // at (0, 0) too — geometrically correct (there IS no chain to spread when
    // the source has nowhere to be relative to centre but centre), but
    // visually it stacked every coating reflection directly on the star and
    // read as a muddy blob rather than a clean point. Fading the whole chain
    // in only once the source has moved perceptibly off-axis sidesteps the
    // degenerate case instead of trying to make it look good.
    const centerDistance = Math.hypot(ndcX, ndcY);
    const centerFade = THREE.MathUtils.smoothstep(centerDistance, CENTER_FADE_START, CENTER_FADE_END);

    if (edgeFade <= 0 || centerFade <= 0) {
      this.hideGhosts();
      return;
    }

    const strength = edgeFade * centerFade * visibility * (GHOST_STRENGTH_FLOOR + bloom * GHOST_STRENGTH_BLOOM_GAIN);

    for (const ghost of this.ghosts) {
      // Ghosts lie on the line through screen centre and the light: at
      // t = 0 on the light itself, t = 0.5 at centre, t = 1 diametrically
      // opposite, and a little beyond for the common overshoot look.
      const factor = 1 - 2 * ghost.spec.t;
      const gx = ndcX * factor;
      const gy = ndcY * factor;

      const localDir = new THREE.Vector3(gx * tanHalfX, gy * tanHalfY, -1).normalize();
      const worldDir = localDir.applyQuaternion(cameraQuaternion);
      ghost.sprite.position.copy(worldDir).multiplyScalar(GHOST_DISTANCE);
      const scale = GHOST_DISTANCE * ghost.spec.size * GHOST_SPRITE_SCALE;
      ghost.sprite.scale.set(scale, scale, 1);
      ghost.material.opacity = ghost.spec.opacity * strength;
      ghost.sprite.visible = strength > GHOST_VISIBILITY_THRESHOLD;
    }
  }

  private hideGhosts(): void {
    for (const ghost of this.ghosts) ghost.sprite.visible = false;
  }

  setColor(color: THREE.Color): void {
    this.uniforms.uColor.value.copy(color);
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    (this.mesh.material as THREE.Material).dispose();
    this.ghostTexture.dispose();
    for (const ghost of this.ghosts) ghost.material.dispose();
  }
}
